import { useEffect, useMemo, useState } from 'react'
import { StatCard } from '@/pages/Organization/Analytics/components/StatCard'
import { GaugeChart } from '@/pages/Organization/Analytics/components/GaugeChart'
import { DonutChart } from '@/pages/Organization/Analytics/components/DonutChart'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { useAuth } from '@/context/AuthContext'
import { adminApi } from '@/utils/api'
import {
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import type {
  AdminDashboard as AdminDashboardType,
  AdminMetricsTrends,
  AdminRiskAnalysis,
  AdminCompletionRate,
} from '@/types/admin'

const mockKpis = [
  { label: 'Total Users', value: '24,310', trend: '+8.2%', muted: 'vs last 30d' },
  { label: 'Organizations', value: '142', trend: '+12%', muted: 'pending 6' },
  { label: 'Assessments Completed', value: '18,902', trend: '+5.4%', muted: 'last 30d' },
  { label: 'High-Risk Couples', value: '312', trend: '-2.1%', muted: 'flagged' },
]

export default function AdminDashboard() {
  const { user } = useAuth()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  const [dashboard, setDashboard] = useState<AdminDashboardType | null>(null)
  const [trends, setTrends] = useState<AdminMetricsTrends>([])
  const [risk, setRisk] = useState<AdminRiskAnalysis | null>(null)
  const [completion, setCompletion] = useState<AdminCompletionRate[]>([])
  const [error, setError] = useState<string | null>(null)
  const [trendPeriod, setTrendPeriod] = useState<'7d' | '30d' | '90d' | '1y'>('7d')

  useEffect(() => {
    const load = async () => {
      try {
        setError(null)
        const [dashRes, trendsRes, riskRes, completionRes] = await Promise.all([
          adminApi.dashboard(),
          adminApi.metricsTrends({ period: trendPeriod }),
          adminApi.riskAnalysis(),
          adminApi.completionRates(),
        ])
        setDashboard((dashRes as { data?: AdminDashboardType }).data || (dashRes as AdminDashboardType))
        const trendsPayload = trendsRes as { data?: { data?: AdminMetricsTrends } }
        setTrends(trendsPayload.data?.data || (trendsRes as { data?: AdminMetricsTrends }).data || (trendsRes as unknown as AdminMetricsTrends))
        setRisk((riskRes as { data?: AdminRiskAnalysis }).data || (riskRes as unknown as AdminRiskAnalysis))
        setCompletion(
          (completionRes as { data?: AdminCompletionRate[] }).data || (completionRes as unknown as AdminCompletionRate[])
        )
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load admin dashboard')
      }
    }
    void load()
  }, [trendPeriod])

  const assessmentRisk = dashboard?.assessmentMetrics?.riskDistribution

  const donutData = useMemo(
    () => {
      const colors = ['#22C55E', '#F59E0B', '#EF4444']
      if (risk?.labels && risk.values) {
        const total = risk.values.reduce((sum, val) => sum + val, 0)
        return risk.labels.map((label, idx) => ({
          label,
          value: risk.values[idx] || 0,
          color: colors[idx] ?? '#94A3B8',
          percentage: total > 0 ? `${Math.round(((risk.values[idx] || 0) / total) * 100)}%` : '0%'
        }))
      }
      if (assessmentRisk) {
        const values = [
          assessmentRisk.low ?? 0,
          assessmentRisk.moderate ?? 0,
          assessmentRisk.high ?? 0,
        ]
        const total = values.reduce((sum, val) => sum + val, 0)
        return ['Low', 'Moderate', 'High'].map((label, idx) => ({
          label,
          value: values[idx] ?? 0,
          color: colors[idx] ?? '#94A3B8',
          percentage: total > 0 ? `${Math.round(((values[idx] ?? 0) / total) * 100)}%` : '0%'
        }))
      }
      return ['Low', 'Medium', 'High'].map((label, idx) => ({
        label,
        value: 0,
        color: colors[idx] ?? '#94A3B8',
        percentage: '0%'
      }))
    },
    [risk, assessmentRisk]
  )

  const barData = useMemo(
    () => {
      if (trends?.length) {
        const mapped = trends.map((t, idx) => ({
          label: t.date
            ? new Date(t.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
            : `Day ${idx + 1}`,
          value: (t as { users?: number; value?: number }).users ?? t.value ?? 0,
        }))
        if (mapped.length <= 30) return mapped
        const step = Math.max(1, Math.ceil(mapped.length / 30))
        return mapped.filter((_, idx) => idx === mapped.length - 1 || idx % step === 0)
      }
      return [
        { label: 'Users', value: dashboard?.systemOverview?.totalUsers ?? 0 },
        { label: 'Organizations', value: dashboard?.systemOverview?.totalOrganizations ?? 0 },
        { label: 'Couples', value: dashboard?.systemOverview?.totalCouples ?? 0 },
        { label: 'Assessments', value: dashboard?.systemOverview?.totalAssessments ?? 0 },
      ]
    },
    [trends, dashboard?.systemOverview]
  )

  const kpis = useMemo(() => {
    const overview = dashboard?.systemOverview
    if (!overview) return mockKpis
    return [
      {
        label: 'Total Users',
        value: (overview.totalUsers ?? 0).toLocaleString(),
        trend: '',
        muted: 'All users',
      },
      {
        label: 'Organizations',
        value: (overview.totalOrganizations ?? 0).toLocaleString(),
        trend: '',
        muted: '',
      },
      {
        label: 'Assessments Completed',
        value: (overview.completedAssessments ?? 0).toLocaleString(),
        trend: '',
        muted: '',
      },
      {
        label: 'High-Risk Couples',
        value: (dashboard.assessmentMetrics?.riskDistribution?.high ?? 0).toLocaleString(),
        trend: '',
        muted: '',
      },
    ]
  }, [dashboard])

  const completionRateFallback = dashboard?.assessmentMetrics?.completionRate ?? 0

  return (
    <div className='min-h-screen bg-[#edeff3]'>
      <div className='p-4 md:p-6 lg:p-8'>
        <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8'>
          {/* Header */}
          <div className='flex items-center justify-between mb-6 sm:mb-8'>
            <div>
              <h1 className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>Admin Dashboard</h1>
              <p className='text-[13px] sm:text-[14px] text-[#4e4d4d] font-normal mt-1'>
                System-wide overview across users, organizations, assessments, and learning.
              </p>
            </div>
            <div className='hidden lg:flex items-center gap-3'>
              <div className='h-[47px] w-[48px] rounded-full bg-[#434343] flex items-center justify-center text-white font-bold text-[16px]'>
                {initials}
              </div>
              <span className='text-[24px] font-bold text-[#353535]'>{displayName}</span>
            </div>
          </div>

          {error && <p className='mb-4 text-[14px] text-red-600'>{error}</p>}

          {/* KPI Cards */}
          <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8'>
            {kpis.map((kpi) => (
              <StatCard
                key={kpi.label}
                title={kpi.label}
                value={kpi.value}
                subtitle={kpi.muted}
                trend={kpi.trend}
              />
            ))}
          </div>

          {/* Charts Row */}
          <div className='grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8'>
            {/* Growth Trends */}
            <div className='xl:col-span-2 bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 sm:p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h3 className='text-[18px] font-bold text-[#151515]'>Growth Trends</h3>
                <select
                  value={trendPeriod}
                  onChange={(e) => setTrendPeriod(e.target.value as typeof trendPeriod)}
                  className='border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[14px] text-[#4e4d4d] bg-white focus:outline-none focus:ring-1 focus:ring-[#FF6600]'
                >
                  <option value='7d'>Last 7 days</option>
                  <option value='30d'>Last 30 days</option>
                  <option value='90d'>Last 90 days</option>
                  <option value='1y'>Last year</option>
                </select>
              </div>
              <div className='h-72 sm:h-80 w-full'>
                <ResponsiveContainer width='100%' height='100%'>
                  <ReBarChart data={barData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray='3 3' vertical={false} />
                    <XAxis dataKey='label' tick={{ fontSize: 10 }} interval={0} height={50} angle={-25} textAnchor='end' />
                    <YAxis allowDecimals={false} />
                    <Tooltip formatter={(value) => value} labelFormatter={(label) => `Date: ${label}`} cursor={{ fill: 'rgba(237,239,243,0.5)' }} />
                    <Bar dataKey='value' fill='#151515' radius={[4, 4, 0, 0]} maxBarSize={28} />
                  </ReBarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Risk Distribution */}
            <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 sm:p-6'>
              <h3 className='text-[18px] font-bold text-[#151515] mb-4'>Risk Distribution</h3>
              <DonutChart data={donutData} />
            </div>
          </div>

          {/* Bottom Row */}
          <div className='grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6'>
            {/* Completion Rates */}
            <div className='xl:col-span-2 bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 sm:p-6'>
              <h3 className='text-[18px] font-bold text-[#151515] mb-4'>Completion Rates</h3>
              <GaugeChart
                value={
                  completion.length
                    ? Math.round(
                        completion.reduce((sum, item) => sum + (item.completionRate ?? 0), 0) / completion.length
                      )
                    : Math.round(completionRateFallback)
                }
                label='Avg Completion'
              />
              <p className='text-[14px] text-[#4e4d4d] mt-4'>
                Compare completion rates across organizations to spot drop-offs and coach admins.
              </p>
            </div>

            {/* System Health */}
            <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 sm:p-6'>
              <h3 className='text-[18px] font-bold text-[#151515] mb-4'>System Health</h3>
              <div className='space-y-4'>
                {[
                  { label: 'Database', status: 'Healthy', color: 'text-[#15803d]' },
                  { label: 'Storage', status: 'Healthy', color: 'text-[#15803d]' },
                  { label: 'WhatsApp', status: 'Degraded', color: 'text-[#d97706]' },
                ].map((item) => (
                  <div key={item.label} className='flex items-center justify-between py-2 border-b border-[#eaecf0] last:border-0'>
                    <span className='text-[14px] text-[#4e4d4d]'>{item.label}</span>
                    <span className={`text-[14px] font-bold ${item.color}`}>{item.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
